import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrAfter);

import { QrStatus } from "@/types/qr";
import { UserContextProps } from "@/contexts/UserContext/types";
import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { SubscriptionStatus } from "@/types/subscription";
import { UserProps, UserStatus } from "@/types/user";
import { getSiteInfo } from "@/sites";

export const userProfileApiToDataTransformer = (profileData: GetProfileResponse): UserProps => {
  const profile = profileData;

  if (!profile) return undefined;

  return {
    accountId: profile.accountId,
    userId: profile.id,
    email: profile.email,
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone,
    locale: profile.locale,
    userStatus: UserStatus[profile.status?.toUpperCase()],
    freeTrialStartsAt: profile.freeTrialStartsAt,
    freeTrialEndsAt: profile.freeTrialEndsAt,
    provider: profile.provider,
    subscription: {
      id: profile.subscription.id,
      subscribed: profile.subscription?.subscribed,
      status: SubscriptionStatus[profile.subscription?.status?.toUpperCase()],
      payAt: profile.subscription?.payAt,
      unsubscribedAt: profile.subscription?.unsubscribedAt,
      requestedUnsubscribedAt: profile.subscription?.requestedUnsubscribedAt,
      plan: profile.subscription?.plan,
    },
    countryIsoCode: profile.countryIsoCode,
    qrStatus:
      profile.subscription?.status?.toUpperCase() === SubscriptionStatus.ACTIVE
        ? QrStatus.ACTIVE
        : QrStatus.DRAFT,
  };
};

export const getRenewData = (userContext: UserContextProps, locale: string = "en-US") => {
  const payAt = userContext.user.subscription?.payAt;

  if (!payAt) return undefined;

  dayjs.extend(localizedFormat);
  const lang = locale.split("-")[0];
  const nextPaymentDate = dayjs(payAt).locale(lang).format("LL");

  return nextPaymentDate;
};
export const getCancelData = (user: UserProps, locale: string = "en-US") => {
  const unsubscriptionDate = user?.subscription?.unsubscribedAt;

  if (!unsubscriptionDate) return undefined;
  dayjs.extend(localizedFormat);

  const lang = locale.split("-")[0];
  const formattedDate = dayjs(unsubscriptionDate).locale(lang).format("LL");

  return formattedDate;
};

export const isSubscriptionActive = (userContext: UserContextProps) => {
  return (
    userContext?.user?.subscription?.status?.toLowerCase() ===
    SubscriptionStatus.ACTIVE.toLowerCase()
  );
};

export const isSubscriptionCancelled = (user: UserProps) => {
  return user?.subscription?.status?.toLowerCase() === SubscriptionStatus.CANCELLED.toLowerCase();
};

export const isSubscriptionRegistered = (userContext: UserContextProps) => {
  return (
    userContext?.user?.subscription?.status?.toLowerCase() ===
    SubscriptionStatus.REGISTERED.toLowerCase()
  );
};

export const isSubscriptionRecorded = (userContext: UserContextProps) => {
  return (
    userContext?.user?.subscription?.status?.toLowerCase() ===
    SubscriptionStatus.RECORDED.toLowerCase()
  );
};

export const isUserRegistered = (userContext: UserContextProps) => {
  return userContext?.user?.userStatus?.toLowerCase() === UserStatus.REGISTERED.toLowerCase();
};

export const isUserSubscribed = (userContext: UserContextProps) => {
  return userContext?.user?.userStatus?.toLowerCase() === UserStatus.SUBSCRIBED.toLowerCase();
};

export const isUserUnsubscribed = (userContext: UserContextProps) => {
  return userContext?.user?.userStatus?.toLowerCase() === UserStatus.UNSUBSCRIBED.toLowerCase();
};

export const isUserAnonymous = (userContext: UserContextProps) => {
  return userContext?.user?.userStatus?.toLowerCase() === UserStatus.ANONYMOUS.toLowerCase();
};

export const isUserNotAuthorized = (userContext: UserContextProps) => {
  return !!(isUserRegistered(userContext) && userContext.user.freeTrialEndsAt);
};

export const isUserFreeTrial = (userContext: UserContextProps) => {
  return userContext?.user?.userStatus?.toLowerCase() === UserStatus.TRIAL.toLowerCase();
};

export const userHasFreeTrialActive = (userContext: UserContextProps): boolean => {
  if (!userContext?.user?.freeTrialEndsAt) return null;

  const freeTrialEndsAt = userContext?.user?.freeTrialEndsAt;

  const freeTrialEndDay = dayjs(freeTrialEndsAt).startOf("day");
  const today = dayjs().startOf("day");

  return freeTrialEndDay.isSameOrAfter(today);
};

export const isUserTrialExpired = (userContext: UserContextProps): boolean => {
  const { isFreeTrial: isFreeTrialSite } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};
  if (!isFreeTrialSite) return false;

  const hasActiveFreeTrial = userHasFreeTrialActive(userContext);
  const userStatus = userContext?.user?.userStatus?.toLowerCase();
  return hasActiveFreeTrial === false &&
    userStatus !== UserStatus.SUBSCRIBED.toLowerCase() &&
    userStatus !== UserStatus.UNSUBSCRIBED.toLowerCase()
    ? true
    : false;
};

export const getRemainingFreeTrialDays = (userContext: UserContextProps): number => {
  if (!userContext?.user?.freeTrialEndsAt) return;
  const freeTrialEndsAt = userContext?.user?.freeTrialEndsAt;

  const newDate = new Date(freeTrialEndsAt);
  const now = new Date();

  return dayjs(newDate).diff(now, "days") + 1;
};

export const hasUserId = (userContext: UserContextProps) => {
  return userContext?.user?.userId;
};

export const nextPaymentDate = (user: UserProps, locale: string = "en-US") => {
  if (!user.subscription.payAt) return;
  dayjs.extend(localizedFormat);

  const lang = locale.split("-")[0];
  const formattedDate = dayjs(user.subscription.payAt).locale(lang).format("LL");

  return formattedDate;
};

export const getUserSubscriptionCountryName = ({
  countryCode,
  lang = "en",
}: {
  countryCode: string;
  lang?: string;
}) => {
  if (!countryCode) return;

  try {
    const regionNames = new Intl.DisplayNames([lang], { type: "region" });
    const region = regionNames.of(countryCode);
    return region;
  } catch {
    return countryCode;
  }
};

export const getUserSubscriptionCountryCode = ({
  countryName,
  lang = "en",
}: {
  countryName: string;
  lang?: string;
}) => {
  const countries = getAllCountries(lang);
  const country = countries.find((c) => c.countryName === countryName);
  return country?.countryCode;
};

export const getAllCountries = (lang = "en") => {
  const A = 65;
  const Z = 90;
  const countryName = new Intl.DisplayNames([lang], { type: "region" });
  const countries: { countryCode: string; countryName: string }[] = [];

  for (let i = A; i <= Z; ++i) {
    for (let j = A; j <= Z; ++j) {
      const code = String.fromCharCode(i) + String.fromCharCode(j);
      const name = countryName.of(code);
      if (code !== name) {
        countries.push({ countryCode: code, countryName: name });
      }
    }
  }
  return countries;
};
